import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft, Phone, Mail, MessageCircle, CalendarDays, Clock, Wallet,
  Star, AlertTriangle, StickyNote, Check, User,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  confirmed: 'bg-blue-50 text-blue-700 border-blue-200',
  scheduled: 'bg-blue-50 text-blue-700 border-blue-200',
  in_progress: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  no_show: 'bg-orange-50 text-orange-700 border-orange-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
};

const formatDate = (d) => {
  if (!d) return '—';
  const date = new Date(`${d}T00:00:00`);
  return Number.isNaN(date.getTime())
    ? d
    : date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const money = (v) => `${Number(v || 0).toFixed(2)} KWD`;

const ClientProfile = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = localStorage.getItem('sessionToken');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notes, setNotes] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesError, setNotesError] = useState('');

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/provider/clients/${clientId}/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
      setNotes(res.data.notes || '');
      setError('');
    } catch (e) {
      setError(e.response?.data?.detail || t('clientProfile.loadFailed', 'Could not load this client'));
    } finally {
      setLoading(false);
    }
  }, [clientId, token]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await axios.put(`${API}/provider/clients/${clientId}/notes`, { notes }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotesSaved(true);
      setNotesError('');
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (e) {
      setNotesError(e.response?.data?.detail || t('clientProfile.noteFailed', 'Could not save the note'));
    } finally {
      setSavingNotes(false);
    }
  };

  const client = data?.client;
  const stats = data?.stats;
  const waNumber = (client?.phone || '').replace(/[\s-]/g, '').replace(/^\+/, '');

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="client-profile-page">
      <div className="mx-auto max-w-4xl px-4 pt-6">
        <button
          onClick={() => navigate('/provider-dashboard?tab=clients')}
          data-testid="client-profile-back-btn"
          className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" /> {t('clientProfile.back', 'Back to clients')}
        </button>

        {loading && (
          <div className="rounded-3xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="client-profile-loading">
            {t('clientProfile.loading', 'Loading client…')}
          </div>
        )}

        {!loading && error && (
          <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 text-sm font-semibold text-rose-700" data-testid="client-profile-error">
            {error}
          </div>
        )}

        {!loading && !error && client && (
          <>
            {/* Identity + contact */}
            <header className="rounded-3xl border border-slate-200 bg-white p-5 sm:p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex min-w-0 items-start gap-4">
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 text-xl font-bold text-white">
                    {client.name?.charAt(0)?.toUpperCase() || <User className="h-6 w-6" />}
                  </div>
                  <div className="min-w-0">
                    <h1 className="truncate text-2xl font-bold text-slate-900" data-testid="client-profile-name">
                      {client.name}
                    </h1>
                    <div className="mt-1 flex flex-wrap items-center gap-2">
                      {client.is_guest && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold uppercase text-slate-600">{t('clientProfile.guest', 'Guest')}</span>
                      )}
                      {client.source === 'imported' && (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-bold uppercase text-violet-700">
                          {t('clientProfile.imported', 'Imported')}{client.imported_from ? ` · ${client.imported_from}` : ''}
                        </span>
                      )}
                      {(client.tags || []).map((tag) => (
                        <span key={tag} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">{tag}</span>
                      ))}
                    </div>
                    <div className="mt-2 space-y-1 text-sm text-slate-600">
                      {client.phone && (
                        <p className="numeric-ltr flex items-center gap-2" data-testid="client-profile-phone">
                          <Phone className="h-3.5 w-3.5 text-slate-400" /> {client.phone}
                        </p>
                      )}
                      {client.email && (
                        <p className="flex items-center gap-2 break-all" data-testid="client-profile-email">
                          <Mail className="h-3.5 w-3.5 text-slate-400" /> {client.email}
                        </p>
                      )}
                      {client.preferred_staff && (
                        <p className="flex items-center gap-2">
                          <Star className="h-3.5 w-3.5 text-slate-400" /> {t('clientProfile.usuallySees', 'Usually sees {{name}}', { name: client.preferred_staff })}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {client.phone && (
                    <>
                      <a
                        href={`tel:${client.phone}`}
                        data-testid="client-profile-call-btn"
                        className="grid h-10 w-10 place-items-center rounded-full bg-blue-50 text-blue-600 hover:bg-blue-100"
                        title="Call"
                      >
                        <Phone className="h-4 w-4" />
                      </a>
                      <a
                        href={`https://wa.me/${waNumber}?text=${encodeURIComponent(`Hi ${client.name}, `)}`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="client-profile-whatsapp-btn"
                        className="grid h-10 w-10 place-items-center rounded-full bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                        title="WhatsApp"
                      >
                        <MessageCircle className="h-4 w-4" />
                      </a>
                    </>
                  )}
                  {client.email && (
                    <a
                      href={`mailto:${client.email}`}
                      data-testid="client-profile-email-btn"
                      className="grid h-10 w-10 place-items-center rounded-full bg-violet-50 text-violet-600 hover:bg-violet-100"
                      title="Email"
                    >
                      <Mail className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            </header>

            {/* Lifetime numbers */}
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="client-profile-stats">
              {[
                { label: t('clientProfile.totalSpent', 'Total spent'), value: money(stats.total_spent), icon: Wallet, testid: 'client-stat-spent' },
                { label: t('clientProfile.visits', 'Visits'), value: stats.completed_visits, icon: Check, testid: 'client-stat-visits' },
                { label: t('clientProfile.upcoming', 'Upcoming'), value: stats.upcoming_count, icon: CalendarDays, testid: 'client-stat-upcoming' },
                { label: t('clientProfile.avgPerVisit', 'Avg per visit'), value: money(stats.average_spend), icon: Star, testid: 'client-stat-average' },
              ].map((card) => {
                const Icon = card.icon;
                return (
                  <div key={card.label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={card.testid}>
                    <Icon className="h-4 w-4 text-slate-400" />
                    <p className="numeric-ltr mt-2 text-xl font-bold text-slate-900">{card.value}</p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
                  </div>
                );
              })}
            </div>

            <div className="numeric-ltr mt-3 flex flex-wrap gap-x-5 gap-y-1 px-1 text-xs text-slate-500" data-testid="client-profile-meta">
              <span>{t('clientProfile.firstVisit', 'First visit')} <span className="numeric-ltr">{formatDate(stats.first_visit)}</span></span>
              <span>{t('clientProfile.lastVisit', 'Last visit')} <span className="numeric-ltr">{formatDate(stats.last_visit)}</span></span>
              {stats.favorite_service && <span>{t('clientProfile.favourite', 'Favourite')}: {stats.favorite_service}</span>}
              {stats.no_shows > 0 && (
                <span className="font-semibold text-orange-600" data-testid="client-profile-noshow-warning">
                  <AlertTriangle className="me-1 inline h-3 w-3" />
                  {t('clientProfile.noShows', '{{count}} no-shows ({{rate}}%)', { count: stats.no_shows, rate: stats.no_show_rate })}
                </span>
              )}
              {stats.cancelled > 0 && <span>{t('clientProfile.cancelledCount', '{{count}} cancelled', { count: stats.cancelled })}</span>}
            </div>

            {/* Upcoming */}
            <section className="mt-6">
              <h2 className="text-base font-bold text-slate-900">{t('clientProfile.upcomingTitle', 'Upcoming appointments')}</h2>
              <div className="mt-3 space-y-3">
                {data.upcoming.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="client-profile-no-upcoming">
                    {t('clientProfile.noUpcoming', 'Nothing booked yet — a good moment to invite them back.')}
                  </div>
                ) : (
                  data.upcoming.map((apt) => (
                    <div
                      key={apt.id}
                      data-testid={`client-upcoming-${apt.id}`}
                      className="flex gap-4 rounded-2xl border border-blue-200 bg-blue-50/40 p-4"
                    >
                      <div className="numeric-ltr w-24 shrink-0 border-e border-blue-100 pe-3 text-center">
                        <p className="text-sm font-bold text-slate-900">{formatDate(apt.date)}</p>
                        <p className="text-xs text-slate-500">{apt.time}</p>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-bold text-slate-900">{apt.service}</p>
                        <p className="numeric-ltr mt-0.5 text-sm text-slate-600">
                          {apt.staff_name || t('clientProfile.anyStaff', 'Any staff')} · {apt.duration} min · {money(apt.fee)}
                        </p>
                        <p className="mt-1 flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                            STATUS_STYLES[apt.status] || 'border-slate-200 bg-slate-100 text-slate-600'
                          }`}>
                            {(apt.status || '').replace(/_/g, ' ')}
                          </span>
                          {apt.reference_number && (
                            <span className="numeric-ltr font-mono text-[11px] text-slate-400">{apt.reference_number}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* History */}
            <section className="mt-6">
              <h2 className="text-base font-bold text-slate-900">
                {t('clientProfile.historyTitle', 'Booking history')} <span className="numeric-ltr text-slate-400">({data.history.length})</span>
              </h2>
              <div className="mt-3 space-y-2">
                {data.history.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="client-profile-no-history">
                    {t('clientProfile.noHistory', 'No past bookings with you yet.')}
                  </div>
                ) : (
                  data.history.map((apt) => (
                    <div
                      key={apt.id}
                      data-testid={`client-history-${apt.id}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-semibold text-slate-900">{apt.service}</p>
                        <p className="numeric-ltr mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                          <Clock className="h-3 w-3" />
                          {formatDate(apt.date)} · {apt.time}
                          {apt.staff_name ? ` · ${apt.staff_name}` : ''}
                          {apt.reference_number ? ` · ${apt.reference_number}` : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                          STATUS_STYLES[apt.status] || 'border-slate-200 bg-slate-100 text-slate-600'
                        }`}>
                          {(apt.status || '').replace(/_/g, ' ')}
                        </span>
                        <span className="numeric-ltr text-sm font-bold text-slate-900">{money(apt.fee)}</span>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </section>

            {/* Private notes */}
            <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5" data-testid="client-profile-notes">
              <h2 className="flex items-center gap-2 text-base font-bold text-slate-900">
                <StickyNote className="h-4 w-4 text-slate-400" /> {t('clientProfile.notesTitle', 'Private notes')}
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                {t('clientProfile.notesHint', 'Only your team sees this — allergies, preferences, anything worth remembering.')}
              </p>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                maxLength={4000}
                data-testid="client-notes-input"
                className="mt-3 w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                placeholder={t('clientProfile.notesPlaceholder', 'e.g. Prefers a short fade, sensitive scalp, always runs 10 minutes late')}
              />
              <div className="mt-3 flex items-center gap-3">
                <button
                  onClick={saveNotes}
                  disabled={savingNotes}
                  data-testid="client-notes-save-btn"
                  className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-50"
                >
                  {savingNotes ? t('clientProfile.saving', 'Saving…') : t('clientProfile.saveNote', 'Save note')}
                </button>
                {notesError && (
                  <span className="text-xs font-semibold text-rose-600" data-testid="client-notes-error">
                    {notesError}
                  </span>
                )}
                {notesSaved && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid="client-notes-saved">
                    <Check className="h-3.5 w-3.5" /> {t('clientProfile.saved', 'Saved')}
                  </span>
                )}
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ClientProfile;
